
import React, { useState, useEffect } from 'react';
import { BookingDetails, FriendStatus } from '../types';
import Logo from './Logo';
import { saveLeadToCloud } from '../services/cloudService';
import PhoneInput from './PhoneInput';

interface Props {
  isOpen: boolean;
  flow: 'pay_now';
  onClose: () => void;
  booking: BookingDetails;
  onComplete: (friends: FriendStatus[], organizerName: string) => void;
}

const MOCK_FRIENDS = [
  { id: '1', name: 'Jake', phone: '+1 555-010-2211' },
  { id: '2', name: 'Sarah', phone: '+1 555-010-3344' },
  { id: '3', name: 'Leo', phone: '+1 555-010-5566' },
  { id: '4', name: 'Emma', phone: '+1 555-010-7788' },
];

const CleroModal: React.FC<Props> = ({ isOpen, flow, onClose, booking, onComplete }) => {
  const [step, setStep] = useState<'phone' | 'name' | 'invite' | 'adjust' | 'preview'>('phone');
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [shares, setShares] = useState<{ [key: string]: number }>({});
  const [organizerShare, setOrganizerShare] = useState<number>(0);

  // Organizer details
  const [organizerName, setOrganizerName] = useState('');
  const [organizerPhone, setOrganizerPhone] = useState('');

  // Initial equal distribution
  useEffect(() => {
    if (selectedFriends.length > 0 && step === 'invite') {
      const totalPeople = selectedFriends.length + 1;
      const equalShare = booking.total / totalPeople;
      const newShares: { [key: string]: number } = {};
      selectedFriends.forEach(id => {
        newShares[id] = equalShare;
      });
      setShares(newShares);
      setOrganizerShare(equalShare);
    }
  }, [selectedFriends, booking.total, step]);

  const toggleFriend = (id: string) => {
    setSelectedFriends(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  };

  // Manual Rebalancing Trigger
  const distributeDifference = () => {
    const currentTotal = organizerShare + Object.values(shares).reduce((a: number, b: number) => a + b, 0);
    const diff = booking.total - currentTotal;

    if (selectedFriends.length > 0) {
      const perFriendAdjustment = diff / selectedFriends.length;
      const newShares = { ...shares };
      selectedFriends.forEach(id => {
        newShares[id] = Math.max(0, newShares[id] + perFriendAdjustment);
      });
      setShares(newShares);
    }
  };

  const handleAmountChange = (id: string | 'organizer', value: string) => {
    const amount = Math.max(0, parseFloat(value) || 0);
    if (id === 'organizer') {
      setOrganizerShare(amount);
    } else {
      setShares(prev => ({ ...prev, [id]: amount }));
    }
  };

  const handlePercentageChange = (id: string | 'organizer', value: string) => {
    const percent = Math.min(100, Math.max(0, parseFloat(value) || 0));
    const amount = (percent / 100) * booking.total;
    if (id === 'organizer') {
      setOrganizerShare(amount);
    } else {
      setShares(prev => ({ ...prev, [id]: amount }));
    }
  };

  const handleNext = () => {
    if (step === 'phone') setStep('name');
    else if (step === 'name') setStep('invite');
    else if (step === 'invite') setStep('adjust');
    else if (step === 'adjust') setStep('preview');
    else {
      // Store lead for email notifications via trusted callable
      void saveLeadToCloud({
        name: organizerName,
        phone: organizerPhone,
        bookingTitle: booking.title,
        totalAmount: booking.total,
        friendCount: selectedFriends.length,
        source: 'demo_modal',
      }).catch(err => console.error("Error saving lead:", err));

      const selectedData: FriendStatus[] = MOCK_FRIENDS
        .filter(f => selectedFriends.includes(f.id))
        .map(f => ({
          ...f,
          status: 'pending',
          amount: shares[f.id]
        }));
      onComplete(selectedData, organizerName);
    }
  };

  if (!isOpen) return null;

  const currentTotal = organizerShare + Object.values(shares).reduce((a: number, b: number) => a + b, 0);
  const diff = booking.total - currentTotal;
  const isBalanced = Math.abs(diff) < 1;

  const firstFriendId = selectedFriends[0];
  const sampleFriendAmount = firstFriendId ? shares[firstFriendId] : (booking.total / (selectedFriends.length + 1 || 1));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
        <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
          <Logo size="md" />
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors">
            <i className="fas fa-times text-gray-400 text-sm"></i>
          </button>
        </div>

        <div className="p-8 md:p-10 overflow-y-auto max-h-[80vh] space-y-8">
          {step === 'invite' && (
            <div className="space-y-8">
              <div className="space-y-1">
                <h3 className="text-xl font-semibold text-gray-900 tracking-tight">Who's joining you?</h3>
                <p className="text-gray-500 text-sm font-medium">Select friends to divide the cost.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {MOCK_FRIENDS.map(friend => (
                  <button
                    key={friend.id}
                    onClick={() => toggleFriend(friend.id)}
                    className={`p-4 rounded-2xl border transition-all flex items-center space-x-4 ${selectedFriends.includes(friend.id) ? 'border-black bg-gray-50' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${selectedFriends.includes(friend.id) ? 'bg-black border-black text-white' : 'border-gray-200'}`}>
                      {selectedFriends.includes(friend.id) && <i className="fas fa-check text-[7px]"></i>}
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900 text-sm">{friend.name}</p>
                      <p className="text-[10px] text-gray-400 font-medium">{friend.phone}</p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex space-x-3">
                <button onClick={() => setStep('name')} className="flex-1 py-4 border border-gray-200 rounded-2xl text-xs font-bold uppercase tracking-widest text-gray-400 hover:bg-gray-50 transition-colors">Back</button>
                <button
                  disabled={selectedFriends.length === 0}
                  onClick={handleNext}
                  className="flex-[2] bg-black text-white py-4 rounded-2xl font-semibold text-base hover:opacity-90 transition-all disabled:opacity-20 shadow-sm"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 'adjust' && (
            <div className="space-y-8">
              <div className="space-y-1">
                <h3 className="text-xl font-semibold text-gray-900 tracking-tight">Adjust shares</h3>
                <p className="text-gray-500 text-sm font-medium">Manually set amounts for each person.</p>
              </div>

              <div className="space-y-3">
                {/* Organizer Input */}
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-[10px] font-bold">Y</div>
                    <span className="text-sm font-semibold text-gray-900">You</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <input
                        type="number"
                        value={((organizerShare / booking.total) * 100).toFixed(0)}
                        onChange={(e) => handlePercentageChange('organizer', e.target.value)}
                        className="w-10 bg-transparent text-right text-[10px] font-bold text-gray-400 focus:text-black outline-none"
                      />
                      <span className="text-[10px] font-bold text-gray-400">%</span>
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                      <input
                        type="number"
                        value={organizerShare.toFixed(0)}
                        onChange={(e) => handleAmountChange('organizer', e.target.value)}
                        className="w-24 pl-6 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm text-right font-semibold focus:border-black outline-none bg-white text-gray-900"
                      />
                    </div>
                  </div>
                </div>

                {/* Friends Inputs */}
                {selectedFriends.map(id => {
                  const friend = MOCK_FRIENDS.find(f => f.id === id);
                  return (
                    <div key={id} className="p-4 bg-white rounded-2xl border border-gray-100 flex items-center justify-between shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-[10px] font-bold border border-gray-50">{friend?.name[0]}</div>
                        <span className="text-sm font-semibold text-gray-900">{friend?.name}</span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={((shares[id] / booking.total) * 100).toFixed(0)}
                            onChange={(e) => handlePercentageChange(id, e.target.value)}
                            className="w-10 bg-transparent text-right text-[10px] font-bold text-gray-400 focus:text-black outline-none"
                          />
                          <span className="text-[10px] font-bold text-gray-400">%</span>
                        </div>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <input
                            type="number"
                            value={shares[id].toFixed(0)}
                            onChange={(e) => handleAmountChange(id, e.target.value)}
                            className="w-24 pl-6 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm text-right font-semibold focus:border-black outline-none bg-white text-gray-900"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Status Banner with Subtle Rebalance Button */}
              <div className={`p-4 rounded-xl flex items-center justify-between transition-colors ${isBalanced ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                <div className="flex items-center space-x-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${isBalanced ? 'bg-green-400' : 'bg-amber-400 animate-pulse'}`}></div>
                  <span className="text-[10px] font-bold uppercase tracking-widest">
                    {isBalanced ? `Balanced • Total $${booking.total}` : `Unallocated: $${diff.toFixed(0)}`}
                  </span>
                </div>
                {!isBalanced && (
                  <button
                    onClick={distributeDifference}
                    className="text-[10px] font-bold uppercase tracking-widest underline decoration-2 underline-offset-4 hover:opacity-70 transition-opacity"
                  >
                    Split difference
                  </button>
                )}
              </div>

              <div className="flex space-x-3">
                <button onClick={() => setStep('invite')} className="flex-1 py-4 border border-gray-200 rounded-2xl text-xs font-bold uppercase tracking-widest text-gray-400 hover:bg-gray-50 transition-colors">Back</button>
                <button
                  disabled={!isBalanced}
                  onClick={handleNext}
                  className="flex-[2] bg-black text-white py-4 rounded-2xl font-semibold text-base hover:opacity-90 transition-all shadow-sm disabled:opacity-10"
                >
                  Confirm split
                </button>
              </div>
            </div>
          )}

          {step === 'phone' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
              <div className="space-y-2 text-center md:text-left">
                <h3 className="text-xl font-semibold text-gray-900 tracking-tight">Enter your phone number</h3>
                <p className="text-gray-500 text-sm font-medium leading-relaxed">
                  We'll use this to send you payment alerts and updates about your group split.
                </p>
              </div>
              <div className="space-y-5">
                <div className="space-y-2">
                  <div className="flex justify-between items-end px-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Phone number</label>
                  </div>
                  <PhoneInput
                    value={organizerPhone}
                    onChange={(val) => setOrganizerPhone(val)}
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  disabled={!organizerPhone}
                  onClick={handleNext}
                  className="w-full bg-black text-white py-4 rounded-2xl font-semibold text-base hover:opacity-90 transition-all shadow-sm disabled:opacity-20"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 'name' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
              <div className="space-y-2 text-center md:text-left">
                <h3 className="text-xl font-semibold text-gray-900 tracking-tight">What's your name?</h3>
                <p className="text-gray-500 text-sm font-medium leading-relaxed">
                  This is how you'll appear to your friends when they receive their split requests.
                </p>
              </div>
              <div className="space-y-5">
                <div className="space-y-2">
                  <div className="flex justify-between items-end px-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Full name</label>
                  </div>
                  <input
                    type="text"
                    placeholder="Alex Smith"
                    value={organizerName}
                    onChange={(e) => setOrganizerName(e.target.value)}
                    className="w-full px-5 py-4 border border-gray-200 rounded-2xl text-base font-medium focus:border-black outline-none bg-white text-gray-900 shadow-sm"
                    autoFocus
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button onClick={() => setStep('phone')} className="flex-1 py-4 border border-gray-200 rounded-2xl text-xs font-bold uppercase tracking-widest text-gray-400 hover:bg-gray-50 transition-colors">Back</button>
                <button
                  disabled={!organizerName}
                  onClick={handleNext}
                  className="flex-[2] bg-black text-white py-4 rounded-2xl font-semibold text-base hover:opacity-90 transition-all shadow-sm disabled:opacity-20"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-8">
              <div className="space-y-1">
                <h3 className="text-xl font-semibold text-gray-900 tracking-tight">Review invitation</h3>
                <p className="text-gray-500 text-sm font-medium">Friends will see this when they open their magic link.</p>
              </div>
              <div className="max-w-[240px] mx-auto border-[6px] border-black rounded-[2.5rem] overflow-hidden shadow-xl bg-white aspect-[9/18]">
                <div className="p-5 space-y-4 flex flex-col h-full bg-white">
                  <div className="flex items-center space-x-2 pb-3 border-b border-gray-50">
                    <Logo size="sm" />
                  </div>
                  <div className="text-center space-y-2 mt-2">
                    <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=50&q=80" className="w-10 h-10 rounded-full mx-auto object-cover" alt="Requester" />
                    <p className="text-[9px] font-semibold text-gray-900 leading-tight">{organizerName || 'Alex'} requested <br /> a share</p>
                  </div>
                  <div className="bg-white border border-gray-50 p-4 rounded-xl text-center shadow-sm">
                    <p className="text-[6px] font-bold text-gray-300 uppercase tracking-widest mb-1">Your Share</p>
                    <p className="text-xl font-bold text-gray-900 tracking-tight">${sampleFriendAmount.toFixed(0)}</p>
                  </div>
                  <div className="mt-auto space-y-2 pb-4">
                    <div className="w-full bg-black h-9 rounded-lg flex items-center justify-center space-x-2 text-white shadow-sm">
                      <i className="fas fa-university text-[8px]"></i>
                      <span className="text-[7px] font-bold uppercase tracking-widest">Connect bank</span>
                    </div>
                    <div className="w-full bg-white border border-gray-100 h-9 rounded-lg flex items-center justify-center space-x-2 text-gray-400">
                      <i className="fas fa-credit-card text-[8px]"></i>
                      <span className="text-[7px] font-bold uppercase tracking-widest">Debit card</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button onClick={() => setStep('adjust')} className="flex-1 py-4 border border-gray-200 rounded-2xl text-xs font-bold uppercase tracking-widest text-gray-400 hover:bg-gray-50 transition-colors">Back</button>
                <button onClick={handleNext} className="flex-[2] bg-black text-white py-4 rounded-2xl font-semibold text-base hover:opacity-90 transition-all shadow-sm">Confirm & Send</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CleroModal;
